import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, concat, EMPTY, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isEqual } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';
import { PresenceActivity } from '@nimbus-global/features/shared/models/enums/presence-activity.enum';
import { PresenceStatus } from '@nimbus-global/features/shared/models/enums/presence-status.enum';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { DutyPresence, UserState } from '@nimbus-library/features/user-state/models/user-state';
import { IUserTaskStateDto } from '@nimbus-library/features/user-state/models/user-task-state.model';
import { NimbusSignalRHub } from '@nimbus-global/features/signalr/nimbusSignalR.hub';
import { IUserHeaderStateNotification } from '@nimbus-global/features/signalr/models/user-header-state-notification.model';
import { IParticipantPresence } from '@nimbus-global/features/ui/models/i-participant-presence.model';
import { PresenceOrigin } from '@nimbus-global/features/shared/models/enums/presence-origin.enum';
import { TenantRealtimeSettingsService } from '@nimbus-global/features/signalr/tenant-realtime-settings.service';
import { IEmergencyManualPresence } from '@nimbus-global/features/signalr/models/tenant-settings-changed-notification.model';
import { UserNotReadyReason, UserNotReadyReasonTooltipMapper } from '../models/not-ready-reason';
import { UserStateApiClient } from './api/user-state.api-client';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class UserTaskStateService {
  private lastTimestamp: string | null = null;

  private readonly manualPresenceUpdatedAt$ = new BehaviorSubject<string | null>(null);
  private readonly userTaskStateInternal$ = new BehaviorSubject<IUserTaskStateDto | null>(null);
  private readonly userTaskState$: Observable<IUserTaskStateDto | null> = combineLatest([
    this.tenantRealtimeSettingsService.getEmergencyManualPresence$().pipe(startWith(null)),
    this.manualPresenceUpdatedAt$.asObservable(),
    this.userTaskStateInternal$.asObservable(),
  ]).pipe(
    map(([emergencyManualPresence, manualPresenceUpdatedAt, state]) =>
      this.applyEmergencyManualPresence(emergencyManualPresence, manualPresenceUpdatedAt, state),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public constructor(
    private readonly authenticationService: IAuthenticationService,
    private readonly signalRHub: NimbusSignalRHub,
    private readonly tenantRealtimeSettingsService: TenantRealtimeSettingsService,
    private readonly translateService: TranslateService,
    private readonly userStateApiClient: UserStateApiClient,
  ) {
    this.initialize();
  }

  public getUserPresence$(): Observable<IParticipantPresence | null> {
    return this.userTaskState$.pipe(
      map((state) => state?.presence ?? null),
      distinctUntilChanged(isEqual),
    );
  }

  public isAvailableForOutboundCall$(): Observable<boolean | null> {
    return this.userTaskState$.pipe(
      map((state) => state?.isAvailableForOutboundCall ?? null),
      distinctUntilChanged(),
    );
  }

  public canGetTask$(): Observable<boolean | null> {
    return this.userTaskState$.pipe(
      map((state) => state?.canGetTasks ?? null),
      distinctUntilChanged(),
    );
  }

  public canGetTask(): boolean | null {
    return this.userTaskState()?.canGetTasks ?? null;
  }

  public getUserState$(): Observable<UserState | null> {
    return this.userTaskState$.pipe(
      map((state) => state?.userState ?? null),
      distinctUntilChanged(),
    );
  }

  public getOnDutyState$(): Observable<DutyPresence | null> {
    return this.userTaskState$.pipe(
      map((state) => state?.onDutyState ?? null),
      distinctUntilChanged(),
    );
  }

  public getRonaStartedAt$(): Observable<string | null> {
    return this.userTaskState$.pipe(
      map((state) => state?.ronaStartedAt ?? null),
      distinctUntilChanged(),
    );
  }

  public setManualPresenceUpdatedAt(presenceUpdatedAt: string): void {
    this.manualPresenceUpdatedAt$.next(presenceUpdatedAt);
  }

  public getNotReadyReasonHint$(): Observable<string> {
    return this.userTaskState$.pipe(
      map((state) =>
        [
          state?.notReadyReason && state?.notReadyReason !== UserNotReadyReason.None
            ? this.translateService.instant(UserNotReadyReasonTooltipMapper[state?.notReadyReason])
            : null,
          state?.notAvailableReason,
        ]
          .filter((x) => !!x)
          .join(' - '),
      ),
      distinctUntilChanged(),
    );
  }

  private initialize(): void {
    this.tenantRealtimeSettingsService.initialize();

    this.authenticationService.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        switchMap((isAuthenticated) => {
          if (!isAuthenticated) {
            this.manualPresenceUpdatedAt$.next(null);
            return EMPTY;
          }
          const { userId } = this.authenticationService.getUserInfo();

          return this.signalRHub.isConnected$.pipe(
            distinctUntilChanged(),
            switchMap((isConnected) => (isConnected ? concat(this.loadInitialState$(userId), this.liveUpdates$(userId)) : EMPTY)),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private userTaskState(): IUserTaskStateDto | null {
    return this.applyEmergencyManualPresence(
      this.tenantRealtimeSettingsService.getEmergencyManualPresence(),
      this.manualPresenceUpdatedAt$.value,
      this.userTaskStateInternal$.value,
    );
  }

  private applyEmergencyManualPresence(
    emergencyManualPresence: IEmergencyManualPresence | null,
    manualPresenceUpdatedAt: string | null,
    state: IUserTaskStateDto | null,
  ): IUserTaskStateDto | null {
    if (!state) {
      return null;
    }
    const presenceUpdatedAt = manualPresenceUpdatedAt ?? state.presenceUpdatedAt;
    const isPresenceStale =
      !!emergencyManualPresence?.mode &&
      !!emergencyManualPresence.changedAt &&
      (!presenceUpdatedAt || new Date(presenceUpdatedAt).getTime() <= new Date(emergencyManualPresence.changedAt).getTime());

    return isPresenceStale
      ? {
          ...state,
          canGetTasks: false,
          userState: UserState.NotSelectable,
          onDutyState: DutyPresence.NotAvailable,
          presence: { availability: PresenceStatus.Offline, activity: PresenceActivity.OffWork },
        }
      : state;
  }

  private loadInitialState$(userId: string): Observable<void> {
    return this.userStateApiClient.getUserTaskState(userId).pipe(
      catchError(() =>
        of<IUserTaskStateDto>({
          userState: UserState.NotSelectable,
          onDutyState: DutyPresence.NotAvailable,
          canGetTasks: false,
          isAvailableForOutboundCall: false,
          presence: null,
          presenceUpdatedAt: null,
          ronaStartedAt: '',
          changedTeamMembership: null,
          timestamp: '',
          notReadyReason: null,
          notAvailableReason: null,
        }),
      ),
      tap((dto) => this.userTaskStateInternal$.next(dto)),
      map(() => void 0),
    );
  }

  private liveUpdates$(userId: string): Observable<void> {
    return this.signalRHub.onUserTaskStateChanged$.pipe(
      filter((event) => event.userId === userId),
      filter((event) => this.isNotOlderThanLastEvent(event)),
      tap((event) => {
        const current = this.userTaskStateInternal$.value;
        const applyPresence = this.shouldApplyPresenceFromEvent(event);
        this.userTaskStateInternal$.next({
          userState: event.userState,
          onDutyState: event.onDutyState,
          canGetTasks: event.canGetTasks,
          notReadyReason: event.notReadyReason,
          notAvailableReason: event.notAvailableReason,
          isAvailableForOutboundCall: event.isAvailableForOutboundCall,
          presence: applyPresence ? event.presence : (current?.presence ?? null),
          presenceUpdatedAt: event.presenceUpdatedAt,
          ronaStartedAt: event.ronaStartedAt,
          changedTeamMembership: event.changedTeamMembership,
          timestamp: event.timestamp,
        });
        this.manualPresenceUpdatedAt$.next(null);
      }),
      catchError(() => EMPTY),
      map(() => void 0),
    );
  }

  private isNotOlderThanLastEvent(event: IUserHeaderStateNotification): boolean {
    if (this.lastTimestamp && new Date(event.timestamp).getTime() < new Date(this.lastTimestamp).getTime()) {
      return false;
    }
    this.lastTimestamp = event.timestamp;
    return true;
  }

  private shouldApplyPresenceFromEvent(event: IUserHeaderStateNotification): boolean {
    if (!event.presence) {
      return false;
    }

    if (!this.tenantRealtimeSettingsService.getEmergencyManualPresenceMode()) {
      return true;
    }

    return event.presence.originatedBy === PresenceOrigin.Manual;
  }
}
